import React from 'react';
import { Autocomplete, TextField, FormControl, FormHelperText, useTheme } from '@mui/material';
import { IDropdownOptions } from '@shared/interface';

interface SelectProps {
  label?: string;
  value: string | number;
  onChange: (value: string | number) => void;
  options: IDropdownOptions[];
  error?: string;
  errorHeight?: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  useOptionId?: boolean; // If true, use optionId instead of value
  'data-testid'?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  error,
  errorHeight = false,
  required = false,
  disabled = false,
  placeholder,
  fullWidth = true,
  size = 'medium',
  useOptionId = false,
  'data-testid': testId,
}) => {
  const theme = useTheme();

  // Find currently selected option object or null
  const selectedOption = options.find((opt) => (useOptionId ? opt.optionId === value : opt.value === value)) || null;

  return (
    <FormControl
      fullWidth={fullWidth}
      error={!!error}
      required={required}
      size={size}
      disabled={disabled}
      data-testid={testId}
    >
      <Autocomplete
        // Pick option objects, not just strings
        options={options}
        getOptionLabel={(option) => option.label}
        value={selectedOption}
        onChange={(_event, newValue) => {
          if (!newValue) {
            onChange(null);
            return;
          }
          onChange(useOptionId ? newValue.optionId ?? null : newValue.value);
        }}
        isOptionEqualToValue={(option, val) =>
          useOptionId ? option.optionId === val.optionId : option.value === val.value
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label || ''}
            placeholder={!selectedOption ? placeholder : ''}
            error={!!error}
            required={required}
            size={size}
            sx={{
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
                '&.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              },
              '& .MuiAutocomplete-input': {
                color: theme.palette.text.primary,
              },
            }}
            // InputLabelProps={{
            //   shrink: true,
            // }}
          />
        )}
        fullWidth
        disabled={disabled}
        disableClearable={false}
        clearOnBlur={false}
        openOnFocus={true}
        autoHighlight
        noOptionsText="No options"
        sx={{
          '& .MuiAutocomplete-popper': {
            // Always appear below
            top: 'auto !important',
          },
        }}
      />
      {(error || errorHeight) && (
        <FormHelperText sx={{ minHeight: '20px', color: error ? theme.palette.error.main : 'transparent' }}>
          {error || ' '}
        </FormHelperText>
      )}
    </FormControl>
  );
};
